import { CircularProgress } from '@material-ui/core';
import { modeUIContext, selectCurrentUsageMode, setIsEditingForm, setIsLoading, showSuccessNotification, UsageMode } from '@moderepo/mode-ui-state';
import clsx from 'clsx';
import produce, { Draft } from 'immer';
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { BaseEntityField, BaseInfoCompProps, capitalizeAll, FieldInputType, getErrorMessage, SelectInputOption } from '@moderepo/mode-app-components';
import { ritzDataContext, selectLoggedInAgent, selectProjectAgent, selectProjectById, selectSudoProjectAccessKey }
    from '@moderepo/mode-data-state-ritz';
import { userAppDataContext, updateEntity, createEntity, clearEntities, fetchEntityById } from '@moderepo/mode-data-state-user-app';
import { AppAPI, Entity, EntityAttributeValueType } from '@moderepo/mode-apis';
import { Agent, AgentRole, ApiError, EntityAttributeParam, EntityAttributeType, EntityClass, ExternalAPI, RequestMethod }
    from '@moderepo/mode-internal-apis';
import { useRitzTranslation } from '../../utils/customHooks';
import { useGetAllProjectHomes, useSelectEntity } from '../../utils/dataStateHooks';
import { EntityInfoComp, EntityInfoCompProps, EntityInfoUpdatableInputs as EntityInstanceUpdatableInputs } from '../../components';
import { getDateTimeAndElapsedTime, getMemorySizeDisplayValue } from '../containerUtils';
import { createEntityClassOptions, createHomeOptions, createTimeZoneOptions } from '../../utils/utils';
import { EntityInstanceInputErrors, isRootEntity, ROOT_ENTITY_PARENT_ID, validateNewEntityInstanceInputs, validateUpdateEntityInstanceInputs }
    from './utils';
import { MAX_ENTITY_ICON_UPLOAD_FILE_SIZE, MAX_ENTITY_IMAGE_UPLOAD_FILE_SIZE, useSelectTranslatedEntityClasses } from '.';



const initializeUserInputs = (
    entity: Entity | undefined,
    entityParent: Entity | undefined,
    homeId: number | undefined,
): EntityInstanceUpdatableInputs => {

    if (entity) {
        // initialize the inputs with the entity object's data
        return {
            homeId     : entity.homeId,
            parent     : entity.parentId !== ROOT_ENTITY_PARENT_ID ? entityParent : undefined,
            entityClass: entity.entityClass,
            entityId   : entity.entityId,
            name       : entity.displaySettings.name,
            description: entity.displaySettings.description,
            timeZone   : entity.timeZone,
            attributes : entity.attributes ? Object.entries(entity.attributes).reduce((result, [key, value]) => {
                if (typeof value === 'object') {
                    // If the attribute' value is an Object e.g. {...} or [...] then we will need to stringify it because we will be
                    // using TextArea to edit this object
                    try {
                        return {
                            ...result,
                            [key]: JSON.stringify(value, undefined, 4),
                        };
                    } catch (error) {
                        // eslint-disable-next-line no-console
                        console.error(error);
                    }
                }
                // If value is not an object then return it as as
                return {
                    ...result,
                    [key]: value,
                };
            }, {}) : undefined,
        };
    }

    // initialize the inputs for a NEW entity with the default values
    return {
        homeId,
        parent  : entityParent,
        // Use the parent's timeZone if a parent is selected. If not, use the local timeZone.
        timeZone: entityParent?.timeZone ?? (new Intl.DateTimeFormat()).resolvedOptions().timeZone,
    };
};



/**
 * Return a object with initial attribute values based on the schema's defaultValues.
 * Given:
 *      attributeSchema: [{
 *          key: "ip", label: "IP Address", type: "string"
 *      }, {
 *          key: "port", label: "Port Number", type: "number", default: 80
 *      }]
 *
 * Return:
 *      {
 *          port: 80
 *      }
 */
const getDefaultAttributeInputValues = (
    attributeSchema: readonly EntityAttributeParam<EntityAttributeValueType>[] | undefined,
): EntityInstanceUpdatableInputs['attributes'] | undefined => {
    return attributeSchema?.reduce((result, attributeConfig) => {
        return {
            ...result,
            [attributeConfig.key]: attributeConfig.defaultValue,
        };
    }, {});
};


/**
 * Attribute input values might not be in the correct type because we use <input /> fields. Attribute type NUMBER can have input value
 * in string format e.g. "123". So we need to go though all the values in the 'attributeValues' and convert them to the correct type
 * based on the schema. NOTE: we will assumed all the input values are validated before calling this function.
 */
const castAttributeInputValues = (
    attributeValues: {
        [key: string]: EntityAttributeValueType | undefined;
    },
    attributeSchema: readonly EntityAttributeParam<EntityAttributeValueType>[] | undefined,
    isNewEntity: boolean,
): {
    [key: string]: EntityAttributeValueType | undefined;
} => {
    const result = {};
    Object.entries(attributeValues).forEach(([key, value]) => {
        // Find the attributeConfig from the schema that has the same key as the attribute input
        const attributeConfig = attributeSchema?.find((attributeConfig) => {
            return attributeConfig.key === key;
        });

        if (attributeConfig !== undefined) {
            // value can be something like these 123, "123", "  123  ", "abc", "  abc  ", "", "  ", true, false, '{"x":1}', "[1, 2, 3]"

            // If value is empty string AND if isNewEntity then treat it as undefined.
            // if NOT isNewEntity then it is ok for the string to be empty because the user might want to change the string from some
            // string to "". Therefore for those cases, we will not treat "" as undefined
            const actualValue = typeof value === 'string' && value.trim().length === 0 && isNewEntity ? undefined : value;

            if (actualValue !== undefined) {
                if (attributeConfig.type === EntityAttributeType.NUMBER) {
                    result[key] = Number(actualValue);
                }
                if (attributeConfig.type === EntityAttributeType.STRING) {
                    result[key] = actualValue.toString().trim();
                }
                if (attributeConfig.type === EntityAttributeType.BOOLEAN) {
                    result[key] = Boolean(actualValue);
                }
                if (attributeConfig.type === EntityAttributeType.ARRAY) {
                    try {
                        result[key] = JSON.parse(actualValue.toString());
                    } catch (error) {
                        // eslint-disable-next-line no-console
                        console.error(error);
                    }
                }
                if (attributeConfig.type === EntityAttributeType.OBJECT) {
                    try {
                        result[key] = JSON.parse(actualValue.toString());
                    } catch (error) {
                        // eslint-disable-next-line no-console
                        console.error(error);
                    }
                }
            } else if (attributeConfig.defaultValue !== undefined) {
                // If actualValue is undefined, that means the user deleted the value from the input field, and if the attributeConfig has a
                // defaultValue then use the default value
                result[key] = attributeConfig.defaultValue;
            }
        }
    });
    return result;
};



/**
 * Given 2 sets of attribute key/value, compare them and return true if they are the same and false otherwise
 */
const areAttributesSetsEqual = (
    attributes1: { [key: string]: EntityAttributeValueType | undefined; } | undefined,
    attributes2: { [key: string]: EntityAttributeValueType | undefined; } | undefined,
): boolean => {
    if (attributes1 !== undefined && attributes2 !== undefined) {

        // Combine all the UNIQUE keys from both attributes. We will use a SET to store them so that we can exclude duplicate keys and then
        // convert it back to ARRAY of keys
        const keySet = Array.from(new Set<string>([...Object.keys(attributes1), ...Object.keys(attributes2)]));

        // For each key, compare the value from attribute1[key] and attribute2[key] and return true if they don't match.
        // For OBJECT and ARRAY values, we will stringify them and compare their string representation.
        const foundUnmatchedKey = keySet.find((key) => {
            const value1 = typeof attributes1[key] === 'object' ? JSON.stringify(attributes1[key]) : attributes1[key];
            const value2 = typeof attributes2[key] === 'object' ? JSON.stringify(attributes2[key]) : attributes2[key];
            return value1 !== value2;
        }) !== undefined;

        // If we found a key that attribute1 and attribute2 have different value then return false, they are different
        return !foundUnmatchedKey;
    }

    // if 1 or both of the attributeSets are undefined then return false if only one of them is undefined
    return attributes1 === attributes2;
};


export interface EntityInfoContainerProps {
    readonly className?: string;
    readonly projectId: number;
    readonly entityId?: string;                              // will be undefined if this container is used for creating new entity

    // For creating a new Entity which a homeId is already provided and the user does not need to select a home
    readonly preselectedHomeId?: number | undefined;

    // For creating a new Entity which a homeId AND parentId is already provided and the user does not need to select a home or a parent
    readonly preselectedParentId?: string | undefined;

    // Props to pass to the EntityInfoComp if there are any custom style
    readonly infoCompProps?: {
        readonly className?: BaseInfoCompProps['className'] | undefined;
        readonly formProps?: BaseInfoCompProps['formProps'] | undefined;
    } | undefined;

    /**
     * The context of which page the user is on. Because this component can be shown in different pages, on a Project page where there is no
     * user or home context or on a User's Home page which has user and home context. This data is not required but it helps create a better
     * URL when we take the user to another page.
     */
    readonly routeContext?: {
        readonly homeId?: number | undefined;
    }

    // Handlers for CREATE entity event.
    readonly createEntityHandlers?: {
        readonly onEntityCreated: ()=> void;
        readonly onCreateEntityCanceled: ()=> void;
    }
}



/**
 * This container is a wrapper for the EntityInfoComp. It contains the LOGICS for the component.
 */
export const EntityInfoContainer: React.FC<EntityInfoContainerProps> = (props: EntityInfoContainerProps) => {

    const { state:uiState, dispatch:uiDispatch } = useContext(modeUIContext);
    const { state:dataState } = useContext(ritzDataContext);
    const { dispatch:appDataDispatch } = useContext(userAppDataContext);
    const project = selectProjectById(dataState, props.projectId);
    const loggedInAgent = selectLoggedInAgent(dataState) as Agent;
    // Get the logged in Agent data for the given project. This is is not the same as loggedInAgent data.
    const projectAgent = loggedInAgent ? selectProjectAgent(dataState, project?.id, loggedInAgent.id) : undefined;
    const usageMode = selectCurrentUsageMode(uiState);
    const canEditEntity = projectAgent?.role === AgentRole.ADMIN && usageMode === UsageMode.MANAGE_DATA;
    const projectKey = selectSudoProjectAccessKey(dataState, props.projectId);


    // Get the entity object for the specified entityId
    const selectedEntity = useSelectEntity(props.projectId, props.entityId, false);

    // Get the PARENT entity ID for the selected entity
    const selectedEntityParentId = useMemo(() => {
        if (props.entityId) {
            // this container is being used for EDITING an existing entity. So we need to get the parentId from the Entity object
            if (selectedEntity && !isRootEntity(selectedEntity)) {
                return selectedEntity.parentId;
            }

            // No parent, return undefined.
            return undefined;
        }

        // This container is being used for CREATING new entity so use the preselectedParentId if there is one
        // If not the parentId will be undefined and the user will need to select a parent entity
        return props.preselectedParentId;
    }, [props.entityId, props.preselectedParentId, selectedEntity]);

    // Once we have the parentId, we can try to get the parentEntity Object
    const selectedEntityParent = useSelectEntity(props.projectId, selectedEntityParentId, false);


    const { trans } = useRitzTranslation(project);
    const [isEditing, setIsEditing] = useState<boolean>(false);
    const [inputErrors, setInputErrors] = useState<EntityInstanceInputErrors>({});
    const isNewEntity = props.entityId === undefined;

    // Get all entity classes belonged to the project
    const entityClasses = useSelectTranslatedEntityClasses(props.projectId, trans);

    // Get all Project's homes
    const allHomes = useGetAllProjectHomes(props.projectId);


    // A cached values of the user inputs
    const [currentUserInputs, setCurrentUserInputs] = useState<EntityInstanceUpdatableInputs>(
        initializeUserInputs(selectedEntity, selectedEntityParent, props.preselectedHomeId ?? props.routeContext?.homeId),
    );


    // If an entityClass is selected in the currentUserInputs, find the entityClass object for that entityClass from the entityClasses array
    // We need the entityClass object so we can get the attributeSchema
    const selectedEntityClass = useMemo((): EntityClass | undefined => {
        if (currentUserInputs.entityClass) {
            return entityClasses?.find((entityClass) => {
                return entityClass.entityClass === currentUserInputs.entityClass;
            });
        }

        return undefined;
    }, [currentUserInputs.entityClass, entityClasses]);


    /**
     * This useEffect is used for re-initializing the currentUserInputs when the 'selectedEntity' changed.
     * This can happen when this component is used for displaying another entity
     */
    useEffect(() => {
        setCurrentUserInputs(initializeUserInputs(selectedEntity, selectedEntityParent, props.preselectedHomeId ?? props.routeContext?.homeId));
    }, [props.preselectedHomeId, props.routeContext, selectedEntity, selectedEntityParent]);


    /**
     * When entityClass changed, we need to update the currentInputs.attributes values because the attribute schema will be different.
     * We will replace the currentInputs.attributes with the defaultValues from the schema
     */
    useEffect(() => {
        if (selectedEntityClass && !selectedEntity?.attributes) {
            setCurrentUserInputs((currentInputs) => {
                return {
                    ...currentInputs,
                    attributes: getDefaultAttributeInputValues(selectedEntityClass.attributeSchema),
                };
            });
        }
    }, [selectedEntity?.attributes, selectedEntityClass]);


    /**
     * Drop down options to be used in the entityClass input field
     */
    const entityClassInputOptions = useMemo((): readonly SelectInputOption<string>[] => {
        const options = createEntityClassOptions(entityClasses);
        return options;
    }, [entityClasses]);


    /**
     * Drop down options to be used in the Home ID input field
     */
    const homeInputOptions = useMemo((): readonly SelectInputOption<number>[] => {
        const options = createHomeOptions(allHomes);
        return options;
    }, [allHomes]);


    // Create the Dropdown Options for the select timeZone input
    const timeZoneOptions = useMemo((): readonly SelectInputOption<string>[] => {
        return createTimeZoneOptions(false, capitalizeAll(trans('label.none')));
    }, [trans]);


    /**
     * Turn on/off edit mode LOCALLY and GLOBALLY
     * @param value - The edit mode true/false
     */
    const onChangeEditingMode = useCallback((value: boolean) => {
        // turn on/off editing mode GLOBALLY so that the system can disable all other components
        uiDispatch(setIsEditingForm(value));

        // turn on/off editing mode LOCALLY so that we can enable this component
        setIsEditing(value);
    }, [uiDispatch]);


    /**
     * Automatically set editing mode to true on component init if props.entityId is undefined which is when user want to create a Entity
     */
    useEffect(() => {
        if (isNewEntity) {
            onChangeEditingMode(true);
        }
    }, [isNewEntity, onChangeEditingMode]);


    /**
     * end editing mode
     */
    const closeEditing = useCallback(() => {
        // Clear errors
        setInputErrors({});

        // also change global edit mode
        onChangeEditingMode(false);
    }, [onChangeEditingMode]);


    /**
     * On user form input change, we will clear the error message for that field.
     */
    const onFormInputChange = useCallback((fieldName: string, newValue: any, isAttribute?: boolean) => {
        setInputErrors(produce(inputErrors, (draft: EntityInstanceInputErrors) => {
            if (isAttribute) {
                // if the field that was updated is an Attribute then delete the attribute from inputErrors.attributes object
                if (draft.attributes) {
                    delete draft.attributes[fieldName];
                }
            } else {
                delete draft[fieldName];
            }
        }));

        setCurrentUserInputs(produce(currentUserInputs, (draft: Draft<EntityInstanceUpdatableInputs>) => {
            // update the value of the field that changed
            if (isAttribute) {
                // if the field that was updated is an Attribute then update the currentInputs.attributes object
                const attributeInputs = draft.attributes ?? {};
                if (!attributeInputs) {
                    draft.attributes = attributeInputs;
                }
                attributeInputs[fieldName] = newValue;
            } else {
                draft[fieldName] = newValue;
            }
        }));
    }, [currentUserInputs, inputErrors]);



    /**
     * Create a new entity with the given user inputs
     */
    const onCreateNewEntity = useCallback(async (inputs: EntityInstanceUpdatableInputs) => {
        const { homeId, parent, entityClass, entityId, name, description, timeZone, attributes } = inputs;
        if (homeId && entityClass && entityId && name && timeZone) {
            try {
                await appDataDispatch(createEntity(props.projectId, {
                    homeId,
                    parentId       : parent?.entityId,
                    entityClass,
                    entityId,
                    timeZone,
                    displaySettings: {
                        name,
                        description,
                    },
                    attributes: attributes ? castAttributeInputValues(attributes, selectedEntityClass?.attributeSchema, true) : undefined,
                }));
                uiDispatch(showSuccessNotification('notif.generic_item_updated_successfully', { term: trans('terms.entity') }));
                closeEditing();


                // Clear all the cached entity data to force UI to reload entity list
                await appDataDispatch(clearEntities(props.projectId));

                if (props.createEntityHandlers?.onEntityCreated) {
                    props.createEntityHandlers.onEntityCreated();
                }
            } catch (error) {
                // ignore error because dataDispatch already handled displaying the errors
            }
        }
    }, [appDataDispatch, closeEditing, props.createEntityHandlers, props.projectId, selectedEntityClass?.attributeSchema, trans, uiDispatch]);



    /**
     * Delete image (icon or image) from an entity
     */
    const deleteEntityImage = useCallback(async (entityId: string, imageTarget: 'icon'| 'image') => {
        try {
            // This call might fail if the image is already deleted by someone else.
            await AppAPI.getInstance().deleteEntityImage(props.projectId, entityId, {
                target: imageTarget,
            }, projectKey?.token);
        } catch (error) {
            // we can ignore delete image error
        }
    }, [projectKey?.token, props.projectId]);


    /**
     * Helper function to Upload an image, icon or image, for an entity. To upload an image for an entity, we will need to make 3 API calls.
     */
    const uploadImageForEntity = useCallback(async (entityId: string, imageTarget: 'icon'| 'image', file: File) => {
        // Make an API call to initiate the upload image flow to get the uploadUrl.
        const uploadInfo = await AppAPI.getInstance().startEntityImageUpload(props.projectId, entityId, {
            target: imageTarget, fileName: file.name,
        }, projectKey?.token);

        // Make External API call to upload the file
        await ExternalAPI.getInstance().sendRequest(RequestMethod.PUT, uploadInfo.uploadUrl, file, { 'Content-Type': 'multipart/form-data' });

        // Make another API call to complete the upload image flow
        await AppAPI.getInstance().completeEntityImageUpload(props.projectId, entityId, {
            target: imageTarget,
        }, projectKey?.token);
    }, [props.projectId, projectKey?.token]);



    /**
     * Update an existing entity with the given user inputs
     */
    const onUpdateEntity = useCallback(async (inputs: EntityInstanceUpdatableInputs, entity: Entity) => {
        const { parent, name, description, iconUrl, imageUrl, attributes: attributeInputs } = inputs;

        if (!selectedEntity) return;

        // To make an entity which is a child of another entity to root entity, we will need to pass 'root' as parentId to the backend. However,
        // for the input, we use 'undefined' as having NO PARENT. We can't pass 'undefined' to backend for parentId because undefined means
        // no change. So, we need to use 'root' if parentId is undefined.
        const newParentId = parent === undefined ? ROOT_ENTITY_PARENT_ID : parent.entityId;



        // If new Icon or Image is set, try to upload the icon/image to S3 first. If successful then complete the upload flow and
        // update the entity attributes.
        uiDispatch(setIsLoading(true));

        if (iconUrl) {
            try {
                await uploadImageForEntity(selectedEntity.entityId, 'icon', iconUrl);
            } catch (error) {
                setInputErrors({
                    ...inputErrors,
                    iconUrl: trans(getErrorMessage((error as ApiError).code)),
                });
                uiDispatch(setIsLoading(false));
                return;
            }
        } else if (iconUrl === null) {
            // If icon is specifically set to null, it means to delete the icon
            await deleteEntityImage(selectedEntity.entityId, 'icon');
        }

        if (imageUrl) {
            try {
                await uploadImageForEntity(selectedEntity.entityId, 'image', imageUrl);
            } catch (error) {
                setInputErrors({
                    ...inputErrors,
                    imageUrl: trans(getErrorMessage((error as ApiError).code)),
                });
                uiDispatch(setIsLoading(false));
                return;
            }
        } else if (imageUrl === null) {
            // If image is specifically set to null, it means to delete the image
            await deleteEntityImage(selectedEntity.entityId, 'image');
        }
        uiDispatch(setIsLoading(false));


        // Only make API call to update entity props and attributes if entity's parentId or name or description changed
        // or there are newAttributes
        const newAttributes = attributeInputs
            ? castAttributeInputValues(attributeInputs, selectedEntityClass?.attributeSchema, false)
            : undefined;

        // compare the newAttributes with the current attributes to see if they are different. If they are the same then we don't need to make
        // API call
        const isAttributeChanged = !areAttributesSetsEqual(newAttributes, selectedEntity.attributes);

        if (
            (name && (newParentId !== entity.parentId || name !== entity.displaySettings.name || description !== entity.displaySettings.description))
            || isAttributeChanged
        ) {
            try {
                // NOTE: Only need to pass the value that is modified. E.g. if only 'name' is modified, then we only need to send 'name'
                await appDataDispatch(updateEntity(props.projectId, selectedEntity.entityId, {
                    parentId       : newParentId !== entity.parentId ? newParentId : undefined,
                    displaySettings: {
                        name       : name !== entity.displaySettings.name ? name : undefined,
                        description: description !== entity.displaySettings.description ? description : undefined,
                    },
                    attributes: isAttributeChanged ? newAttributes : undefined,
                }));
                uiDispatch(showSuccessNotification('notif.generic_item_updated_successfully', { term: trans('terms.entity') }));
                closeEditing();

                if (newParentId !== entity.parentId) {
                    // If parentId changed, we need to clear the entities from the data state to force the UI to reload entities
                    appDataDispatch(clearEntities(props.projectId));
                }
            } catch (error) {
                // ignore error because dataDispatch already handled displaying the errors
            }
        } else {
            // input values are the same so no need to make api call
            closeEditing();

            // If we didn't update entity's name or description but if we updated icon or image then we need to refetch entity data
            if (iconUrl !== undefined || imageUrl !== undefined) {
                appDataDispatch(fetchEntityById(props.projectId, selectedEntity.entityId));
            }
        }
    }, [appDataDispatch, closeEditing, deleteEntityImage, inputErrors, props.projectId, selectedEntity, selectedEntityClass?.attributeSchema,
        trans, uiDispatch, uploadImageForEntity]);



    /**
     * Handler for when the user click Save input from the Entity form.
     * If we are in creating new entity mode, call onCreateEntity. Else update the current entity
     */
    const onFormSubmit = useCallback(async (inputs: EntityInstanceUpdatableInputs) => {
        if (!project) {
            return;
        }


        // Validate inputs and get the set of errors
        const newErrors = !selectedEntity
            ? validateNewEntityInstanceInputs(inputs, selectedEntityClass?.attributeSchema, trans)
            : validateUpdateEntityInstanceInputs(inputs, selectedEntity, selectedEntityClass?.attributeSchema, trans);

        setInputErrors(newErrors);


        // Go through all the values in the newErrors object to find a non undefined value. If there is at least 1 non undefined value,
        // that mean there is an error.
        const hasErrors = Object.values(newErrors).some((error) => {
            return error !== undefined;
        });


        // If there are not errors, we can continue CREATE a Entity or updating an existing Entity
        if (!hasErrors) {
            if (isNewEntity) {
                onCreateNewEntity(inputs);
            } else if (selectedEntity) {
                onUpdateEntity(inputs, selectedEntity);
            }
        }
    }, [isNewEntity, onCreateNewEntity, onUpdateEntity, project, selectedEntity, trans, selectedEntityClass]);



    const onCancelEdit = useCallback(() => {
        closeEditing();

        // Dispatch an event to let the parent container know the CREATE entity flow is canceled
        if (isNewEntity && props.createEntityHandlers?.onCreateEntityCanceled) {
            props.createEntityHandlers.onCreateEntityCanceled();
        }

        // Reset the cached user input with the selectedEntity's data
        const initialInputs = initializeUserInputs(
            selectedEntity, selectedEntityParent, props.routeContext?.homeId,
        );

        // Get the entity's attributes values OR default attribute values if entity DOES NOT have attribute values.
        const attributeInputs = selectedEntity?.attributes ?? getDefaultAttributeInputValues(selectedEntityClass?.attributeSchema);
        setCurrentUserInputs({
            ...initialInputs,
            attributes: attributeInputs,
        });
    }, [closeEditing, isNewEntity, props.createEntityHandlers, props.routeContext?.homeId, selectedEntity, selectedEntityClass?.attributeSchema,
        selectedEntityParent]);



    /**
     * Create the input fields based on the selectedEntityClass's attributeSchema. We will create 1 input field for each attribute
     * and return a map of input fields which keys are attribute's key. For example
     *
     *      Given: attributeSchema = [{
     *          key: 'ip',
     *          label: 'IP Address',
     *          type: 'string'
     *          ...
     *      }, {
     *          key: 'port',
     *          label: Port Number',
     *          type: 'number',
     *          ...
     *      }]
     *
     *      Create: attributeFields = {
     *          ip: {
     *              label: 'IP Address'
     *              controlType: "text",
     *              inputType: "text",
     *              error: '...',
     *              ...
     *          },
     *          port: {
     *              label: 'IP Address'
     *              controlType: "text",
     *              inputType: "number",
     *              error: '...',
     *              ...
     *          }
     *      }
     */
    const attributeInputFields = useMemo((): EntityInfoCompProps['fields']['attributes'] => {

        if (selectedEntityClass?.attributeSchema && selectedEntityClass.attributeSchema.length > 0) {
            const inputFields = {};

            // For each attributeConfig, create a new input field and add it to the map of inputFields
            selectedEntityClass.attributeSchema.forEach((attributeConfig) => {
                const inputControlType = ((): FieldInputType => {
                    if (attributeConfig.type === EntityAttributeType.BOOLEAN) {
                        return FieldInputType.CHECKBOX;
                    }
                    if (attributeConfig.type === EntityAttributeType.ARRAY || attributeConfig.type === EntityAttributeType.OBJECT) {
                        return FieldInputType.TEXT_AREA;
                    }
                    return FieldInputType.TEXT;
                })();

                const inputField: BaseEntityField<EntityAttributeValueType> = {
                    editable   : canEditEntity,
                    label      : attributeConfig.label,
                    description: attributeConfig.description,
                    required   : attributeConfig.required,
                    controlType: inputControlType,
                    inputType  : attributeConfig.type === EntityAttributeType.NUMBER ? 'number' : 'text',
                    options    : attributeConfig.options?.map((option): SelectInputOption<EntityAttributeValueType> => {
                        return {
                            label: option.label, value: option.value,
                        };
                    }),
                    value: currentUserInputs.attributes?.[attributeConfig.key],
                    error: inputErrors.attributes?.[attributeConfig.key] !== undefined
                        ? trans(inputErrors.attributes[attributeConfig.key] as string)
                        : undefined,
                };
                inputFields[attributeConfig.key] = inputField;
            });

            return inputFields;
        }

        return undefined;
    }, [canEditEntity, currentUserInputs.attributes, inputErrors.attributes, selectedEntityClass?.attributeSchema, trans]);


    /**
     * Create the input settings for the EntityInfoComp and cache it
     */
    const inputsSettings = useMemo((): EntityInfoCompProps['fields'] => {

        return {
            projectId: {
                label       : capitalizeAll(trans('field.project_id.label')),
                displayValue: selectedEntity?.projectId?.toString(),
                hideOnEdit  : true,
            },
            homeId: {
                label     : capitalizeAll(trans('field.home_id.label')),
                value     : currentUserInputs.homeId,
                // only editable if creating new entity AND homeId is not preselected
                editable  : canEditEntity && isNewEntity && !props.preselectedHomeId,
                required  : true,
                options   : homeInputOptions,
                // hide the input in edit mode if this input is not editable
                hideOnEdit: !(isNewEntity && !props.preselectedHomeId),
                error     : inputErrors.homeId ? trans(inputErrors.homeId) : '',
            },
            parent: {
                label   : capitalizeAll(trans('field.parent_entity.label')),
                value   : currentUserInputs.parent,
                hidden  : !currentUserInputs.homeId,
                // editable if in UPDATE entity mode. If in CREATE entity mode, only editable parentId is not preselected
                editable: canEditEntity && (!isNewEntity || (isNewEntity && !props.preselectedParentId)),
                required: false,
                error   : inputErrors.parent ? trans(inputErrors.parent) : '',
                texts   : {
                    clearButtonLabel : capitalizeAll(trans('entity.action.make_as_root')),
                    cancelButtonLabel: capitalizeAll(trans('label.cancel')),
                    selectButtonLabel: capitalizeAll(trans('label.select')),
                    emptyValueDisplay: capitalizeAll(trans('entity.label.root')),
                },
                entitiesListingContainerProps: {
                    projectId        : props.projectId,
                    homeId           : currentUserInputs.homeId,
                    // Disable the entity itself and all the descendants from bring selected as parent
                    disabledEntityIds: selectedEntity ? [selectedEntity.entityId] : undefined,
                },
            },
            entityClass: {
                label     : capitalizeAll(trans('field.entity_class.label')),
                value     : currentUserInputs.entityClass,
                editable  : canEditEntity && isNewEntity,
                required  : true,
                hidden    : !currentUserInputs.homeId,
                options   : entityClassInputOptions,
                hideOnEdit: !isNewEntity,
                error     : inputErrors.entityClass ? trans(inputErrors.entityClass) : '',
            },
            entityId: {
                label     : capitalizeAll(trans('field.entity_id.label')),
                value     : selectedEntity?.entityId,
                required  : true,
                editable  : canEditEntity && isNewEntity,
                hideOnEdit: !isNewEntity,
                hidden    : !currentUserInputs.homeId,
                error     : inputErrors.entityId ? trans(inputErrors.entityId) : '',
            },
            name: {
                label   : capitalizeAll(trans('field.name.label')),
                value   : selectedEntity?.displaySettings.name,
                required: true,
                editable: canEditEntity,
                hidden  : !currentUserInputs.homeId,
                error   : inputErrors.name ? trans(inputErrors.name) : '',
            },
            description: {
                label   : capitalizeAll(trans('field.description.label')),
                value   : selectedEntity?.displaySettings.description,
                required: false,
                editable: canEditEntity,
                hidden  : !currentUserInputs.homeId,
                error   : inputErrors.description ? trans(inputErrors.description) : '',
            },
            iconFileName: {
                label     : capitalizeAll(trans('field.file_name.label')),
                value     : selectedEntity?.displaySettings.iconData?.fileName ?? capitalizeAll(trans('label.none')),
                required  : false,
                editable  : false,
                hideOnEdit: true,
                hidden    : !currentUserInputs.homeId || isNewEntity,
            },
            iconUrl: {
                label      : capitalizeAll(trans('label.image')),
                value      : selectedEntity?.displaySettings.iconData?.url,
                required   : false,
                editable   : canEditEntity,
                hidden     : !currentUserInputs.homeId || isNewEntity,
                placeholder: trans('input.image.max_size_error', {
                    file_size: getMemorySizeDisplayValue(MAX_ENTITY_ICON_UPLOAD_FILE_SIZE, trans),
                }),
                error: inputErrors.iconUrl ? trans(inputErrors.iconUrl, {
                    file_size: getMemorySizeDisplayValue(MAX_ENTITY_ICON_UPLOAD_FILE_SIZE, trans),
                }) : '',
            },
            imageFileName: {
                label     : capitalizeAll(trans('field.file_name.label')),
                value     : selectedEntity?.displaySettings.imageData?.fileName ?? capitalizeAll(trans('label.none')),
                required  : false,
                editable  : false,
                hideOnEdit: true,
                hidden    : !currentUserInputs.homeId || isNewEntity,
            },
            imageUrl: {
                label      : capitalizeAll(trans('label.image')),
                value      : selectedEntity?.displaySettings.imageData?.url,
                required   : false,
                editable   : canEditEntity,
                hidden     : !currentUserInputs.homeId || isNewEntity,
                placeholder: trans('input.image.max_size_error', {
                    file_size: getMemorySizeDisplayValue(MAX_ENTITY_IMAGE_UPLOAD_FILE_SIZE, trans),
                }),
                error: inputErrors.imageUrl ? trans(inputErrors.imageUrl, {
                    file_size: getMemorySizeDisplayValue(MAX_ENTITY_IMAGE_UPLOAD_FILE_SIZE, trans),
                }) : '',
            },
            timeZone: {
                label     : capitalizeAll(trans('field.time_zone.label')),
                value     : currentUserInputs.timeZone,
                options   : timeZoneOptions,
                editable  : canEditEntity && isNewEntity,
                hideOnEdit: !isNewEntity,
                hidden    : !currentUserInputs.homeId,
                error     : inputErrors.timeZone ? trans(inputErrors.timeZone) : '',
            },
            createdAt: {
                label       : capitalizeAll(trans('field.created_date.label')),
                displayValue: selectedEntity ? getDateTimeAndElapsedTime(selectedEntity.createdAt, trans) : '',
                hidden      : !currentUserInputs.homeId,
                hideOnEdit  : true,
            },
            updatedAt: {
                label       : capitalizeAll(trans('field.updated_date.label')),
                displayValue: selectedEntity ? getDateTimeAndElapsedTime(selectedEntity.updatedAt, trans) : '',
                hidden      : !currentUserInputs.homeId,
                hideOnEdit  : true,
            },
            attributes: attributeInputFields,
        };
    }, [trans, selectedEntity, currentUserInputs.homeId, currentUserInputs.parent, currentUserInputs.entityClass, currentUserInputs.timeZone,
        canEditEntity, isNewEntity, props.preselectedHomeId, props.preselectedParentId, props.projectId, homeInputOptions, inputErrors.homeId,
        inputErrors.parent, inputErrors.entityClass, inputErrors.entityId, inputErrors.name, inputErrors.description, inputErrors.iconUrl,
        inputErrors.imageUrl, inputErrors.timeZone, entityClassInputOptions, timeZoneOptions, attributeInputFields]);



    return (
        <>
            {project && (selectedEntity || isNewEntity) && (
                <EntityInfoComp
                    className={clsx(props.className, props.infoCompProps?.className, isEditing && 'editing')}
                    formProps={props.infoCompProps?.formProps}
                    isEditing={isEditing}
                    title={capitalizeAll(trans(isNewEntity ? 'entity.info_comp.create_entity.title' : 'entity.info_comp.title'))}
                    icon={trans('icons.entity')}
                    fields={inputsSettings}
                    actions={canEditEntity ? {
                        startEdit: {
                            onClick: () => {
                                onChangeEditingMode(true);
                            },
                        },
                        cancelEdit: {
                            label  : capitalizeAll(trans('label.cancel')),
                            onClick: onCancelEdit,
                        },
                        saveUpdate: {
                            label  : isNewEntity ? capitalizeAll(trans('label.create')) : capitalizeAll(trans('label.save')),
                            onClick: onFormSubmit,
                        },
                    } : undefined}
                    texts={{
                        iconGroupTitle      : capitalizeAll(trans('label.icon')),
                        imageGroupTitle     : capitalizeAll(trans('label.image')),
                        attributesGroupTitle: capitalizeAll(trans('entity.label.attribute', { count: 2 })),
                    }}
                    onFieldClicked={() => {
                        onChangeEditingMode(true);
                    }}
                    onFormInputChange={onFormInputChange}
                />
            )}

            {(!project || (!isNewEntity && !selectedEntity)) && (
                <CircularProgress />
            )}
        </>

    );
};
